import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from '../services/local-storage.service';

export type ConfigKey =
    | 'columnsPerBreakpoint'
    | 'minColumnSize'
    | 'maxBreakpoint'
    | 'breakpointIncrement'
    | 'gap'
    | 'aspectRatioWidth'
    | 'aspectRatioHeight'
    | 'homeLayoutMode'
    | 'gridLayout';

export type DefaultConfig = {
    columnsPerBreakpoint: number;
    minColumnSize: number;
    maxBreakpoint: number;
    breakpointIncrement: number;
    gap: number;
    aspectRatioWidth: number;
    aspectRatioHeight: number;
    homeLayoutMode: HomeLayoutMode;
    gridLayout: GridLayout;
};

@Injectable({
    providedIn: 'root'
})
export class ResponsiveService {
    private local = inject(LocalStorageService);

    private subjects: Record<ConfigKey, BehaviorSubject<any>> = {} as Record<ConfigKey, BehaviorSubject<any>>;;

    private defaultConfig: DefaultConfig = {
        columnsPerBreakpoint: 1,
        minColumnSize: 520,
        maxBreakpoint: 10000,
        breakpointIncrement: 800,
        gap: 20,
        aspectRatioWidth: 90,
        aspectRatioHeight: 52,
        homeLayoutMode: HomeLayoutMode.Fluid,
        gridLayout: { columns: 4, rows: 5 },
    };

    constructor() {
        this.initializeSubjects();
    }

    private initializeSubjects() {
        (Object.keys(this.defaultConfig) as ConfigKey[]).forEach((key) => {
            const localValue = this.local.getItem(key);
            let initialValue;
            // Manejar el caso especial de homeLayoutMode
            if (key === 'homeLayoutMode' && localValue !== null) {
                initialValue = localValue as HomeLayoutMode === HomeLayoutMode.Grid ? HomeLayoutMode.Grid : HomeLayoutMode.Fluid;; // No usamos JSON.parse para valores de tipo cadena como 'fluid' o 'grid'
            } else {
                // Para otros valores, intentamos parsear como JSON
                initialValue = localValue !== null ? JSON.parse(localValue) : this.defaultConfig[key];
            }
            this.subjects[key] = new BehaviorSubject(initialValue);
        });
    }

    private updateSubject(key: ConfigKey, value: any): void {
        this.subjects[key].next(value);
        this.local.setItem(key, JSON.stringify(value));
    }

    getSubject<T>(key: ConfigKey): Observable<T> {
        return this.subjects[key].asObservable();
    }

    setSubject<T>(key: ConfigKey, value: T): void {
        this.updateSubject(key, value);
    }

    reset() {
        (Object.keys(this.defaultConfig) as ConfigKey[]).forEach((key) => {
            this.subjects[key].next(this.defaultConfig[key]);
            this.local.removeItem(key);
        });
    }

    // Métodos específicos para homeLayoutMode y gridLayout
    validateHomeLayoutMode(): HomeLayoutMode {
        const mode = this.local.getItem('homeLayoutMode');
        return mode === HomeLayoutMode.Grid ? HomeLayoutMode.Grid : HomeLayoutMode.Fluid;
    }

    getHomeLayoutModeStatus(): Observable<HomeLayoutMode> {
        return this.getSubject<HomeLayoutMode>('homeLayoutMode');
    }

    setHomeLayoutModeStatus(mode: HomeLayoutMode): void {
        this.setSubject('homeLayoutMode', mode);
    }

    getGridLayout(): Observable<GridLayout> {
        return this.getSubject<GridLayout>('gridLayout');
    }

    setGridLayout(grid: GridLayout): void {
        this.setSubject('gridLayout', grid);
    }

}

export enum HomeLayoutMode {
    Fluid = 'fluid',
    Grid = 'grid',
}


export interface GridLayout {
    columns: number,
    rows: number
}


